import { skipToken } from "@reduxjs/toolkit/query";

import { Option } from "api/common/types/common.types";
import { useGetProductFeaturesQuery } from "api/memberPortal/features/productFeatures/productFeatures.api";
import { ProductFeature } from "api/memberPortal/features/productFeatures/productFeatures.types";
import { usePcmPpDropdownOptionsQuery } from "api/memberPortal/project/protectionProfiles/protectionProfiles.api";
import { useGetPcmByRidQuery } from "api/public/pcm/pcm.api";
import { PcmData } from "api/public/pcm/pcm.types";
import { useGetPcmPpOptionsQuery } from "api/public/pcm/pp/pcmPp.api";
import { useGetAllNonUsProducerSchemesQuery } from "api/public/schemes/schemes.api";
import { Scheme } from "api/public/schemes/schemes.types";

const SCHEMES = [] as Scheme[];
const PP_OPTIONS = [] as Option[];
const PCM_PP_OPTIONS = [] as Option[];
const PRODUCT_FEATURES = [] as ProductFeature[];
const DEFAULT_PCM = undefined as PcmData | undefined;

/**
 * Hook for fetching initial/static form data.
 * Cascading data (products, labs, maintenances) is handled by usePcmCascadingData
 * which subscribes to form state.
 */
export const usePcmFormData = (pcmRid: number | undefined) => {
  // Fetch PCM data if editing (pcmRid is provided)
  const { data: pcm = DEFAULT_PCM, isFetching: isFetchingPcm } =
    useGetPcmByRidQuery(pcmRid as number, { skip: !pcmRid });

  const { data: schemes = SCHEMES, isFetching: isFetchingSchemes } =
    useGetAllNonUsProducerSchemesQuery();

  const { data: ppOptions = PP_OPTIONS, isFetching: isFetchingPpOptions } =
    usePcmPpDropdownOptionsQuery();

  // Fetch existing product features for PCM when editing
  const {
    data: existingFeatures = PRODUCT_FEATURES,
    isFetching: isFetchingFeatures,
  } = useGetProductFeaturesQuery(pcmRid ? { pcm_id: pcmRid } : skipToken);

  // Fetch existing PCM protection profiles when editing
  const { data: pcmPpOptions = PCM_PP_OPTIONS, isFetching: isFetchingPcmPps } =
    useGetPcmPpOptionsQuery(pcmRid ? { pcm: pcmRid } : skipToken);

  //TODO: Add in file fetching here for existing files

  const isLoading =
    isFetchingPcm ||
    isFetchingSchemes ||
    isFetchingPpOptions ||
    isFetchingFeatures ||
    isFetchingPcmPps;

  return {
    pcm,
    schemes,
    ppOptions,
    pcmPpOptions,
    existingFeatures,
    isLoading,
  };
};
